/**
 * Transport seam for the frozen world protocol.
 *
 * ONE implementation, one interface, one payload shape (`ClientMessage` / `ServerMessage` JSON,
 * exactly as `packages/shared` defines them):
 *
 * - `ColyseusTransport`: `@colyseus/sdk` matchmaking + seat + room transport, with our protocol riding
 *   on `room.send(type, payload)` / `room.onMessage(type, handler)`. The FULL message object is sent as
 *   the payload (including its `t` discriminator) so a server that validates with
 *   `isClientMessage(payload)` and a server that destructures `{ v, name }` from the payload both work
 *   unchanged. Incoming payloads without a `t` field get it back from the message type.
 *
 * There is deliberately no second implementation. The authority upgrades ONLY connections that speak
 * the Colyseus envelope (matchmaking seat -> `/<processId>/<roomId>?sessionId=...` socket ->
 * `JOIN_ROOM`), so a plain `WebSocket` `send`ing our JSON frames is never upgraded into a room, is
 * never greeted with a `welcome`, and could only ever look like a connection the client does not have.
 * A join that fails is therefore terminal for `NetClient` (status `error`), not something to route
 * around - see the header of `client.ts`.
 *
 * The layer is deliberately thin: it owns no state, only framing, so `NetClient` stays testable.
 */

import { decodeMessage, type ClientMessage, type ServerMessage } from "@rogueworlds/shared";
import { Client, type Room } from "@colyseus/sdk";

export interface TransportEvents {
  readonly onMessage: (message: ServerMessage) => void;
  readonly onClose: (reason: string) => void;
}

export interface Transport {
  /** There is exactly one transport; the field exists so the HUD can report what carries the protocol. */
  readonly kind: "colyseus";
  connect(): Promise<void>;
  send(message: ClientMessage): void;
  dispose(): void;
}

/** Colyseus room name of the world shard. One room = one world shard (ADR-0021). */
export const WORLD_ROOM = "world";

/** Normalizes an endpoint (absolute `ws(s)://` URL or a page-relative path) into a `URL`. */
function normalizeEndpoint(endpoint: string): URL | undefined {
  const base = typeof location === "undefined" ? "http://localhost:5173/" : location.href;
  try {
    const url = new URL(endpoint, base);
    if (url.protocol === "http:") {
      url.protocol = "ws:";
    } else if (url.protocol === "https:") {
      url.protocol = "wss:";
    }
    return url;
  } catch {
    return undefined;
  }
}

/**
 * SDK endpoint: the endpoint's ORIGIN only, because matchmaking lives at `/matchmake/*` and the room
 * socket at `/<processId>/<roomId>` - i.e. the path of the world socket must not be part of the base
 * URL. The `ws:`/`wss:` scheme this produces is what the SDK expects: it derives `secure` from it and
 * builds its HTTP matchmaking URL (`http`/`https`) itself.
 */
export function sdkEndpointUrl(endpoint: string): string {
  const url = normalizeEndpoint(endpoint);
  if (url === undefined) {
    return endpoint;
  }
  url.pathname = "/";
  url.search = "";
  url.hash = "";
  return url.toString();
}

/** Rebuilds a full protocol frame from a typed message `(type, payload)` pair. */
export function frameFromTyped(type: string | number, payload: unknown): ServerMessage | undefined {
  let candidate: unknown = payload;
  // The authority sends `client.send(type, encodeMessage(msg))`, i.e. the payload is the JSON TEXT
  // frame, not an object (see CONTRACTS.md, "apps/server"). Both forms are accepted here so the
  // client is correct against the server as built and against a future schema-typed payload.
  if (typeof candidate === "string") {
    try {
      candidate = JSON.parse(candidate) as unknown;
    } catch {
      return undefined;
    }
  }
  if (
    candidate === null ||
    typeof candidate !== "object" ||
    typeof (candidate as { t?: unknown }).t !== "string"
  ) {
    if (typeof type !== "string") {
      return undefined;
    }
    const fields: Record<string, unknown> =
      candidate !== null && typeof candidate === "object" ? (candidate as Record<string, unknown>) : {};
    candidate = { t: type, ...fields };
  }
  try {
    return decodeMessage<ServerMessage>(JSON.stringify(candidate));
  } catch (error) {
    console.warn(`[net] rejected ${String(type)} frame: ${error instanceof Error ? error.message : String(error)}`);
    return undefined;
  }
}

export class ColyseusTransport implements Transport {
  readonly kind: "colyseus" = "colyseus";

  private room: Room | undefined = undefined;
  private readonly client: Client;
  private readonly events: TransportEvents;
  private readonly name: string;
  private disposed = false;

  constructor(endpoint: string, name: string, events: TransportEvents) {
    this.client = new Client(sdkEndpointUrl(endpoint));
    this.name = name;
    this.events = events;
  }

  async connect(): Promise<void> {
    const room = await this.client.joinOrCreate(WORLD_ROOM, { name: this.name });
    if (this.disposed) {
      // The join lost the race against `dispose()` (a connect timeout in `NetClient`): the seat exists
      // but nobody wants it. Give it straight back instead of registering listeners, which would let a
      // late `welcome` reach a client that has already reported the join as failed.
      room.removeAllListeners();
      void room.leave(true).catch(() => undefined);
      return;
    }
    this.room = room;
    room.onMessage("welcome", (payload: unknown) => this.deliver("welcome", payload));
    room.onMessage("snapshot", (payload: unknown) => this.deliver("snapshot", payload));
    room.onMessage("chunk", (payload: unknown) => this.deliver("chunk", payload));
    room.onMessage("ack", (payload: unknown) => this.deliver("ack", payload));
    room.onMessage("blockAck", (payload: unknown) => this.deliver("blockAck", payload));
    room.onMessage("pong", (payload: unknown) => this.deliver("pong", payload));
    room.onMessage("error", (payload: unknown) => this.deliver("error", payload));
    room.onError((code: number, message?: string) => {
      this.events.onClose(`room error ${code}${message === undefined ? "" : `: ${message}`}`);
    });
    room.onLeave((code: number, reason?: string) => {
      this.events.onClose(`left room (code ${code}${reason === undefined ? "" : `, ${reason}`})`);
    });
    room.onDrop((code: number, reason?: string) => {
      this.events.onClose(`dropped (code ${code}${reason === undefined ? "" : `, ${reason}`})`);
    });
  }

  send(message: ClientMessage): void {
    // The whole frame goes as the payload: the type is also the `t` discriminator (see the header).
    this.room?.send(message.t, message);
  }

  dispose(): void {
    this.disposed = true;
    const room = this.room;
    this.room = undefined;
    if (room !== undefined) {
      room.removeAllListeners();
      void room.leave(true).catch(() => undefined);
    }
  }

  private deliver(type: string, payload: unknown): void {
    const message = frameFromTyped(type, payload);
    if (message !== undefined) {
      this.events.onMessage(message);
    }
  }
}
